import React, {type Dispatch,
    type SetStateAction,
    useCallback,
} from 'react';

import {FormInput} from 'src/components/input';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {ACCESS_RIGHTS} from 'src/hooks/access-rights';

import {FormSection} from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import Label from '@pb/kit-component-label';
import {Textarea} from '@pb/kit-component-textarea-v2';

import {LoginPageCustomizationSkeleton} from './login-page-customization.skeleton';

import type {MicroSiteBaseInfo} from '../../typedef';
import type {AccessRight} from 'src/hooks/access-rights/constants';
import type {Error} from 'src/openapi/admin/models/Error';


type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    data: MicroSiteBaseInfo,
    setData: Dispatch<SetStateAction<MicroSiteBaseInfo>>,
    trackChange: (parameter: string) => void,
    readonly?: boolean,
};

export const LoginPageCustomizationSection = (props: Props) => {
    const {
        isLoading,
        loadingError,
        data,
        setData,
        trackChange,
        readonly,
    } = props;

    const changeLoginPageInstructions = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prevState => ({
            ...prevState,
            loginPageInstructions: e.target.value,
        }));
    }, [setData]);

    const blurLoginPageInstructions = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
        const loginPageInstructions = e.currentTarget.value.trim();
        setData(prevState => ({
            ...prevState,
            loginPageInstructions,
        }));
        trackChange('Login Page Instructions');
    }, [setData, trackChange]);

    const changeAccountPageInstructions = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setData(prevState => ({
            ...prevState,
            accountPageInstructions: e.target.value,
        }));
    }, [setData]);

    const blurAccountPageInstructions = useCallback((e: React.FocusEvent<HTMLTextAreaElement>) => {
        const accountPageInstructions = e.currentTarget.value.trim();
        setData(prevState => ({
            ...prevState,
            accountPageInstructions,
        }));
        trackChange('Create Account Page Instructions');
    }, [setData, trackChange]);

    const changeUsernamePrompt = useCallback((loginUserNamePrompt: string) => {
        setData(prevState => ({
            ...prevState,
            loginUserNamePrompt: loginUserNamePrompt || '',
        }));
        trackChange('Login\\Registration Username Prompt');
    }, [setData, trackChange]);

    const changePasswordPrompt = useCallback((loginPasswordPrompt: string) => {
        setData(prevState => ({
            ...prevState,
            loginPasswordPrompt: loginPasswordPrompt || '',
        }));
        trackChange('Login Registration Username Prompt');
    }, [setData, trackChange]);

    return (
        <Skeleton shown={isLoading} view={<LoginPageCustomizationSkeleton/>}>
            <ServerResponseHandler
                smallView={true}
                sectionName="Microsite Login Page Customization"
                accessRight={ACCESS_RIGHTS.MICROSITES_SCREEN_VIEW as AccessRight}
                serverError={loadingError}>
                <FormSection headline="Login & Registration Page Customization">
                    <Group direction="v" alignItems="start">
                        <Item style={{width: 300}}>
                            <Label>Login Page Instructions</Label>
                            <Textarea
                                disabled={readonly}
                                placeholder="Login Page Instructions"
                                maxLength={1000}
                                value={data.loginPageInstructions || ''}
                                onChange={changeLoginPageInstructions}
                                onBlur={blurLoginPageInstructions}
                            />
                        </Item>
                        <Group gap="l" alignItems="center">
                            <FormInput
                                width={300}
                                maxLength={100}
                                readOnly={readonly}
                                label="Login\Registration Username Prompt"
                                placeholder="Login\Registration Username Prompt"
                                value={data.loginUserNamePrompt || ''}
                                onBlur={changeUsernamePrompt}
                            />
                            <FormInput
                                width={300}
                                maxLength={100}
                                readOnly={readonly}
                                label="Login Password Prompt"
                                placeholder="Login Password Prompt"
                                value={data.loginPasswordPrompt || ''}
                                onBlur={changePasswordPrompt}
                            />
                        </Group>
                        <Item style={{width: 300}}>
                            <Label>Create Account Page Instructions</Label>
                            <Textarea
                                disabled={readonly}
                                placeholder="Create Account Page Instructions"
                                maxLength={1000}
                                value={data.accountPageInstructions || ''}
                                onChange={changeAccountPageInstructions}
                                onBlur={blurAccountPageInstructions}
                            />
                        </Item>
                    </Group>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
